import React, { useState } from "react";
import { toast } from "sonner";
import { Search, Download, Plus, X } from "lucide-react";

export function PageTitle({ title, subtitle, action }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-heading text-2xl font-bold text-charcoal sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-mutedtext">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

export function Panel({ title, children, action, className = "" }) {
  return (
    <section className={`rounded-md border border-subtle bg-white ${className}`}>
      {(title || action) && (
        <div className="flex items-center justify-between border-b border-subtle px-4 py-3">
          <h3 className="font-heading text-base font-bold text-charcoal">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Btn({ children, variant = "primary", className = "", icon: Icon, ...props }) {
  const styles = {
    primary: "bg-forest text-white hover:bg-forestdark",
    outline: "border border-forest text-forest hover:bg-forest/5",
    ghost: "text-charcoal hover:bg-forest/5",
    danger: "bg-polishred text-white hover:bg-red-800",
    subtle: "bg-secondary text-charcoal hover:bg-accent",
  };
  return (
    <button className={`inline-flex items-center justify-center gap-2 rounded-md px-3 py-2 text-sm font-semibold transition-colors disabled:opacity-50 ${styles[variant]} ${className}`} {...props}>
      {Icon && <Icon className="h-4 w-4" aria-hidden="true" />} {children}
    </button>
  );
}

export function Field({ label, children, hint, required }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-charcoal">{label}{required && <span className="text-polishred"> *</span>}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-mutedtext">{hint}</span>}
    </label>
  );
}

export function Input(props) {
  return <input {...props} className={`w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-charcoal focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/40 ${props.className || ""}`} />;
}
export function Textarea(props) {
  return <textarea {...props} className={`w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-charcoal focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/40 ${props.className || ""}`} />;
}
export function Select({ children, ...props }) {
  return <select {...props} className={`w-full rounded-md border border-input bg-white px-3 py-2 text-sm text-charcoal focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/40 ${props.className || ""}`}>{children}</select>;
}

export function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className={`relative z-10 my-8 w-full rounded-lg bg-white shadow-xl ${wide ? "max-w-3xl" : "max-w-xl"}`}>
        <div className="flex items-center justify-between border-b border-subtle px-5 py-3">
          <h3 className="font-heading text-lg font-bold text-charcoal">{title}</h3>
          <button onClick={onClose} aria-label="Close" data-testid="modal-close"><X className="h-5 w-5 text-mutedtext" /></button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function DataTable({ columns, rows, empty = "No data.", testid }) {
  return (
    <div className="overflow-x-auto rounded-md border border-subtle bg-white">
      <table className="w-full min-w-[600px] text-sm" data-testid={testid}>
        <thead>
          <tr className="border-b border-subtle bg-secondary/50 text-left">
            {columns.map((c) => (
              <th key={c.key} scope="col" className="px-3 py-2 font-semibold text-charcoal">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr><td colSpan={columns.length} className="px-3 py-8 text-center text-mutedtext">{empty}</td></tr>
          ) : rows.map((r, i) => (
            <tr key={r.id || i} className="border-b border-subtle last:border-0 hover:bg-secondary/30">
              {columns.map((c) => <td key={c.key} className="px-3 py-2 align-top text-charcoal">{c.render ? c.render(r) : r[c.key]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Toolbar({ search, setSearch, onExport, children }) {
  return (
    <div className="mb-4 flex flex-wrap items-center gap-3">
      {setSearch && (
        <div className="relative flex-1 min-w-[180px]">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-mutedtext" aria-hidden="true" />
          <input data-testid="search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search…"
            className="w-full rounded-md border border-input bg-white py-2 pl-9 pr-3 text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/40" />
        </div>
      )}
      {children}
      {onExport && <Btn variant="outline" icon={Download} onClick={onExport} data-testid="export-btn">CSV</Btn>}
    </div>
  );
}

export function exportCsv(filename, rows) {
  if (!rows.length) { toast.error("Nothing to export"); return; }
  const keys = Object.keys(rows[0]).filter((k) => typeof rows[0][k] !== "object");
  const csv = [keys.join(","), ...rows.map((r) => keys.map((k) => `"${String(r[k] ?? "").replace(/"/g, '""')}"`).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export { Plus, toast };
